import type { Field, Option } from "../components/EntityForm";

/**
 * Shared vocabularies and nested-create form definitions.
 *
 * Every form that references another entity (company, contact, participant,
 * location, competency) offers "+ Add new…" in its dropdown, opening a small
 * create form without leaving the one you were filling in. The definitions
 * live here once so a company created from the lead form is the same shape
 * as one created from the Companies page.
 */

export const SERVICE_TYPES = [
    "Clinical Nursing Care", "High Intensity Support", "NDIS Consulting",
    "Staff Training & Education", "Wound Care", "Medication Management",
    "Tracheostomy/Ventilator", "PEG/Enteral Feeding", "Other",
];

export const LEAD_SOURCES = [
    "Referral", "Website", "LinkedIn", "Cold Outreach",
    "Conference", "Existing Client", "Other",
];

export const INDUSTRIES = [
    "NDIS Provider", "Support Coordination", "Plan Management", "Allied Health",
    "Healthcare", "Disability Services", "Government", "Other",
];

export const JOB_TITLES = [
    "Support Coordinator", "Specialist Support Coordinator", "Plan Manager",
    "Case Manager", "Service Manager", "Operations Manager", "Team Leader",
    "Registered Nurse", "Clinical Nurse Manager", "Rostering Coordinator",
    "Intake Coordinator", "Director", "CEO", "Office Administrator",
];

export const toOptions = (values: string[]): Option[] =>
    values.map((v) => ({ value: v, label: v }));

/** What a lead can actually be about, in this business. Typing stays free. */
export const OPPORTUNITIES = [
    "New nursing services engagement", "High-intensity supports contract",
    "Complex care package", "Wound care program", "Overnight nursing supports",
    "Medication management service", "Tracheostomy/ventilator supports",
    "PEG/enteral feeding supports", "Catheter & continence care",
    "Staff training package", "NDIS consulting engagement",
    "Service agreement renewal", "Additional hours — existing participant",
];

export const TASK_SUGGESTIONS = [
    "Medication stock check", "Restock PPE and consumables",
    "Equipment maintenance check", "First aid kit check", "Fire safety check",
    "Site safety inspection", "Vehicle safety check", "Care plan review",
    "Documentation audit", "Deep clean", "Handover meeting",
    "Supervision visit", "Incident follow-up", "Welcome pack preparation",
];

/** The HR staff-file template: TCAS00001_LastNameFirstName_Role. */
export const EMPLOYEE_DOC_CATEGORIES = [
    "01 Recruitment — Application / CV",
    "01 Recruitment — Interview notes",
    "01 Recruitment — Reference checks",
    "01 Recruitment — Offer & acceptance",
    "02 Pre-employment — Personal identification",
    "02 Pre-employment — Right to work",
    "02 Pre-employment — NDIS worker screening",
    "02 Pre-employment — NDIS worker training modules",
    "02 Pre-employment — Police check",
    "02 Pre-employment — WWCC",
    "02 Pre-employment — Vaccination records",
    "02 Pre-employment — Other licences",
    "03 Employment contract details",
    "04 Qualifications — Certificates",
    "04 Qualifications — First aid & CPR",
    "04 Qualifications — Mandatory training records",
    "04 Qualifications — Clinical competencies",
    "05 Onboarding & induction",
    "06 Performance — Performance reviews",
    "06 Performance — Supervision notes",
    "06 Performance — Annual appraisal",
    "06 Performance — Improvement plans",
    "06 Performance — Disciplinary actions",
    "06 Performance — WHS documents",
    "07 Payroll & insurance",
    "08 Employee variations",
    "09 Incidents & complaints",
    "10 Termination & post-employment",
    "11 Correspondence",
];

/** The provider/participant filing tree — subject to change, so data. */
export const PARTICIPANT_DOC_CATEGORIES = [
    "001 Service agreements & support plans",
    "002 Risk management",
    "003 Emergency management",
    "004 Progress notes & reports",
    "005 Complaints, feedback & surveys",
    "006 Invoices",
    "007 Plan details",
    "008 Medication management",
    "009 Behaviour support",
    "010 Incidents management",
    "011 Other",
    "012 Mealtime management",
    "013 Sleep log",
];

/** ABN Lookup deep link — verify the number against the ABR. */
export const abnLookup = (abn?: string | null) => ({
    label: "Verify on ABN Lookup",
    href: abn
        ? `https://abr.business.gov.au/Search/Run?SearchText=${encodeURIComponent(abn)}`
        : "https://abr.business.gov.au/",
});

/** Contact dropdown options carrying their company for cascading selects. */
export const contactOptionsOf = (
    contacts: Array<{ id: string; first_name: string; last_name: string; company_id: string | null }>
): Option[] =>
    contacts.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}`, parent: c.company_id }));

const COMPANY_KINDS: Option[] = [
    { value: "provider", label: "Provider" },
    { value: "plan_manager", label: "Plan Manager" },
    { value: "support_coordinator", label: "Support Coordinator" },
    { value: "referrer", label: "Referrer" },
    { value: "supplier", label: "Supplier" },
    { value: "other", label: "Other" },
];

export const companyCreateFields = (): Field[] => [
    { name: "name", label: "Organisation name", kind: "text", required: true },
    { name: "kind", label: "Type", kind: "select", required: true, half: true, options: COMPANY_KINDS },
    { name: "abn", label: "ABN", kind: "text", half: true, placeholder: "XX XXX XXX XXX",
      helpLink: { label: "Verify on ABN Lookup", href: "https://abr.business.gov.au/" } },
    { name: "phone", label: "Phone", kind: "tel", half: true, placeholder: "08 XXXX XXXX" },
    { name: "email", label: "Email", kind: "email", half: true },
    { name: "suburb", label: "Suburb", kind: "text", half: true },
    { name: "website", label: "Website", kind: "text", half: true, placeholder: "https://example.com.au" },
    { name: "ndis_registered", label: "NDIS registered provider", kind: "checkbox" },
];

export const contactCreateFields = (companyOptions: Option[]): Field[] => [
    { name: "first_name", label: "First name", kind: "text", required: true, half: true },
    { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
    { name: "job_title", label: "Role / title", kind: "select", half: true,
      optionsFrom: { path: "/job-titles", addLabel: "Add job title" } },
    { name: "company_id", label: "Organisation", kind: "select", half: true,
      options: companyOptions,
      addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() } },
    { name: "email", label: "Email", kind: "email", half: true },
    { name: "phone", label: "Phone", kind: "tel", half: true },
];

export const locationCreateFields = (): Field[] => [
    { name: "name", label: "Location name", kind: "text", required: true },
    { name: "type", label: "Type", kind: "select", half: true,
      optionsFrom: { path: "/location-types", addLabel: "Add location type" } },
    { name: "suburb", label: "Suburb", kind: "text", half: true },
    { name: "address", label: "Address", kind: "text" },
    { name: "region", label: "Region", kind: "text", half: true },
];

export const competencyCreateFields = (): Field[] => [
    { name: "name", label: "Competency name", kind: "text", required: true },
    { name: "category", label: "Category", kind: "select", half: true,
      optionsFrom: { path: "/competency-categories", addLabel: "Add category" } },
    { name: "default_valid_months", label: "Valid for (months)", kind: "number", half: true, min: 1,
      help: "Leave blank for a competency that does not expire." },
];

export const participantQuickCreateFields = (): Field[] => [
    { name: "first_name", label: "First name", kind: "text", required: true, half: true },
    { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
    { name: "ndis_number", label: "NDIS number", kind: "text", half: true },
    { name: "primary_phone", label: "Phone", kind: "tel", half: true },
];

/** Dropdown label for a created contact row. */
export const personLabel = (row: Record<string, unknown>): string =>
    String(row.full_name ?? [row.first_name, row.last_name].filter(Boolean).join(" "));

/**
 * Every announcement channel the database actually supports, in one place so
 * a feed tab list and the compose form can't drift apart the way the
 * Dashboard's own channel tabs once did (missing "ndis_update" entirely,
 * silently hiding anything posted to it).
 */
export const ANNOUNCEMENT_CHANNELS: Array<{
    value: "system_news" | "ndis_update" | "organisation" | "ndis_web" | "ndis_facebook" | "ndis_twitter" | "our_facebook";
    label: string; sourceUrl?: string; sourceLabel?: string;
}> = [
    { value: "system_news", label: "System News" },
    { value: "ndis_update", label: "NDIS Update" },
    { value: "ndis_web", label: "NDIS Website", sourceUrl: "https://www.ndis.gov.au/news", sourceLabel: "ndis.gov.au/news" },
    { value: "ndis_facebook", label: "NDIS Facebook", sourceUrl: "https://www.facebook.com/NDISAus", sourceLabel: "facebook.com/NDISAus" },
    { value: "ndis_twitter", label: "NDIS on X", sourceUrl: "https://x.com/NDIS", sourceLabel: "x.com/NDIS" },
    { value: "our_facebook", label: "Our Facebook", sourceUrl: "https://www.facebook.com/trustcaresupport", sourceLabel: "Our page" },
    { value: "organisation", label: "Organisation" },
];
